import { CalendarDate } from '@internationalized/date';
import { Calendar, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

type CalendarDateLike = {
  toString: () => string;
};

interface MonthPickerProps {
  value?: CalendarDate | null;
  onChange?: (value: CalendarDate | null) => void;
  minValue?: CalendarDateLike | null;
  maxValue?: CalendarDateLike | null;
  isDisabled?: boolean;
  placeholder?: string;
  className?: string;
}

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const parseYearMonth = (
  value?: CalendarDateLike | null
): { year: number; month: number } | null => {
  if (!value) return null;
  const [year, month] = value.toString().split('-').map(Number);
  return year && month ? { year, month } : null;
};

// A single-month picker matching the visual language of the Digital Shelf
// `DatePicker` (same trigger button / popover shell classes), used two at a
// time (start + end) instead of one combined range calendar.
const MonthPicker = ({
  value,
  onChange,
  minValue,
  maxValue,
  isDisabled,
  placeholder = 'Select month',
  className,
}: MonthPickerProps) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const min = parseYearMonth(minValue);
  const max = parseYearMonth(maxValue);
  const selected = value ? { year: value.year, month: value.month } : null;

  const [viewYear, setViewYear] = useState(
    selected?.year ?? max?.year ?? min?.year ?? new Date().getFullYear()
  );

  useEffect(() => {
    if (open) setViewYear(selected?.year ?? viewYear);
    // Only re-sync the visible year when the popover opens.
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClickAway = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickAway);
    return () => document.removeEventListener('mousedown', handleClickAway);
  }, [open]);

  const isMonthDisabled = (year: number, month: number) => {
    if (min && (year < min.year || (year === min.year && month < min.month)))
      return true;
    if (max && (year > max.year || (year === max.year && month > max.month)))
      return true;
    return false;
  };

  const canGoPrevYear = !min || viewYear > min.year;
  const canGoNextYear = !max || viewYear < max.year;

  const label = selected
    ? `${MONTH_NAMES[selected.month - 1]} ${selected.year}`
    : placeholder;

  const handleSelectMonth = (month: number) => {
    onChange?.(new CalendarDate(viewYear, month, 1));
    setOpen(false);
  };

  return (
    <div className={clsx('relative', className)} ref={wrapperRef}>
      <button
        type="button"
        disabled={isDisabled}
        onClick={() => setOpen((o) => !o)}
        className={clsx(
          'group flex min-h-[46px] w-full items-center gap-3 rounded-lg border border-transparent bg-indigo-100 px-4 py-[14px] text-left shadow-xs transition-colors',
          isDisabled
            ? 'pointer-events-none opacity-60'
            : 'cursor-pointer hover:bg-indigo-500',
          open ? 'bg-indigo-100' : ''
        )}
      >
        <Calendar className="size-4 shrink-0 text-indigo-800 transition-colors group-hover:text-white" />
        <span className="min-w-0 flex-1 text-sm font-medium text-indigo-800 transition-colors group-hover:text-white">
          {label}
        </span>
        <ChevronDown className="size-4 shrink-0 text-indigo-800 transition-colors group-hover:text-white" />
      </button>

      {open && (
        <div className="absolute top-[calc(100%+8px)] left-0 z-50 w-[280px] overflow-hidden rounded-[20px] border border-slate-200 bg-white p-4 shadow-[0_16px_38px_rgba(15,23,42,0.12)]">
          <div className="mb-3 flex items-center justify-between">
            <button
              type="button"
              disabled={!canGoPrevYear}
              onClick={() => setViewYear((y) => y - 1)}
              className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 disabled:pointer-events-none disabled:opacity-30"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm font-semibold text-slate-800">
              {viewYear}
            </span>
            <button
              type="button"
              disabled={!canGoNextYear}
              onClick={() => setViewYear((y) => y + 1)}
              className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 disabled:pointer-events-none disabled:opacity-30"
            >
              <ChevronRight size={16} />
            </button>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {MONTH_NAMES.map((name, idx) => {
              const month = idx + 1;
              const disabled = isMonthDisabled(viewYear, month);
              const isSelected =
                selected?.year === viewYear && selected?.month === month;
              return (
                <button
                  key={name}
                  type="button"
                  disabled={disabled}
                  onClick={() => handleSelectMonth(month)}
                  className={clsx(
                    'rounded-[10px] py-2 text-sm font-medium transition-colors',
                    disabled
                      ? 'pointer-events-none text-gray-300'
                      : isSelected
                        ? 'bg-indigo-600 text-white'
                        : 'text-slate-700 hover:bg-indigo-50'
                  )}
                >
                  {name}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthPicker;
